import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map, take, switchMap } from 'rxjs/operators';
import { AuthService } from '../../features/auth/services/auth.service';
import { AuthInitializerService } from '../../features/auth/services/auth-initializer.service';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserStateService } from '../services/user-state.service';

/**
 * Adverts: platform admin or manufacturer-scoped user.
 */
export const manufacturerUserGuard: CanActivateFn = (route, state) => {
  const showDebugInfo = environment.showDebugInfo;
  const router = inject(Router);
  const authService = inject(AuthService);
  const userStateService = inject(UserStateService);
  const authInitializer = inject(AuthInitializerService);

  if (!authInitializer.isAuthReady()) {
    return of(authInitializer.initializeAuth()).pipe(
      switchMap(() =>
        checkManufacturerAccess(authService, userStateService, router, showDebugInfo)
      )
    );
  }

  return checkManufacturerAccess(
    authService,
    userStateService,
    router,
    showDebugInfo
  );
};

function checkManufacturerAccess(
  authService: AuthService,
  userStateService: UserStateService,
  router: Router,
  showDebugInfo: boolean
): Observable<boolean> {
  return authService.checkAuthenticated().pipe(
    take(1),
    switchMap((isAuthenticated) => {
      if (!isAuthenticated) {
        router.navigate(['/login']);
        return of(false);
      }

      return userStateService.state$.pipe(
        take(1),
        map((state) => {
          const hasAccess = state.isAdmin || !!state.manufacturerId;
          if (showDebugInfo) {
            console.log('Manufacturer user guard:', {
              isAdmin: state.isAdmin,
              manufacturerId: state.manufacturerId,
              hasAccess,
            });
          }
          if (!hasAccess) {
            router.navigate(['/unauthorized']);
            return false;
          }
          return true;
        })
      );
    })
  );
}
